import React, { useState, useCallback } from 'react';
import { TicketAvatar } from '../../styled-comps';
import {
  UserResultCard,
  UserResultHeader,
  UserResultName,
  UserResultSummary,
  UserResultDetails,
  UserDetailRow,
  UserDetailLabel,
  UserDetailValue,
  CopyBtn,
  SearchTicketItem,
  TicketRow,
  TicketIdMono,
  TicketStatusBadge,
  TicketSubjectText,
  TicketTimeText,
  LoadMoreTicketsBtn,
  UserTicketsSection,
  UserTicketsHeader,
  UserTicketsList,
  ExpandArrow,
  EmptySearch,
  EmptySub,
  getTicketWait,
} from './styled';
import Icon from '../icon';

const INITIAL_TICKET_COUNT = 3;
const LOAD_MORE_COUNT = 10;

const CopyField = ({ label, value }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(
    e => {
      e.stopPropagation();
      navigator.clipboard
        .writeText(value)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        })
        .catch(() => {});
    },
    [value],
  );

  return (
    <UserDetailRow>
      <UserDetailLabel>{label}</UserDetailLabel>
      <UserDetailValue>{value}</UserDetailValue>
      <CopyBtn onClick={handleCopy} title="Copy">
        <Icon name={copied ? 'check' : 'copy'} size={11} />
      </CopyBtn>
    </UserDetailRow>
  );
};

const TicketItem = ({ ticket, isActive, onClick }) => (
  <SearchTicketItem $active={isActive} onClick={onClick}>
    <TicketRow>
      <TicketIdMono>#{ticket.id}</TicketIdMono>
      <TicketStatusBadge $status={ticket.status}>
        {ticket.status || 'Open'}
      </TicketStatusBadge>
    </TicketRow>
    {ticket.category && <TicketSubjectText>{ticket.category}</TicketSubjectText>}
    <TicketTimeText>{getTicketWait(ticket)}</TicketTimeText>
  </SearchTicketItem>
);

const CustomerTickets = ({ tickets, activeTicketId, onTicketClick }) => {
  const [visibleCount, setVisibleCount] = useState(INITIAL_TICKET_COUNT);

  if (!tickets || tickets.length === 0) {
    return (
      <UserTicketsList>
        <EmptySearch>
          <EmptySub>No tickets raised yet</EmptySub>
        </EmptySearch>
      </UserTicketsList>
    );
  }

  const visible = tickets.slice(0, visibleCount);
  const hasMore = visibleCount < tickets.length;

  return (
    <UserTicketsList>
      {visible.map(ticket => (
        <TicketItem
          key={ticket.id}
          ticket={ticket}
          isActive={ticket.id === activeTicketId}
          onClick={() => onTicketClick(ticket)}
        />
      ))}
      {hasMore && (
        <LoadMoreTicketsBtn
          onClick={e => {
            e.stopPropagation();
            setVisibleCount(prev => prev + LOAD_MORE_COUNT);
          }}
        >
          Load {Math.min(LOAD_MORE_COUNT, tickets.length - visibleCount)} more tickets
        </LoadMoreTicketsBtn>
      )}
    </UserTicketsList>
  );
};

const UserCard = ({ customer, expanded, onToggle, activeTicketId, onTicketClick }) => (
  <UserResultCard $expanded={expanded}>
    <UserResultHeader onClick={onToggle}>
      <TicketAvatar>{(customer.name || '--').slice(0, 2).toUpperCase()}</TicketAvatar>
      <UserResultName>{customer.name || 'Unknown'}</UserResultName>
      <UserResultSummary>
        {(customer.tickets?.items || []).length} ticket
        {(customer.tickets?.items || []).length !== 1 ? 's' : ''}
      </UserResultSummary>
      <ExpandArrow $expanded={expanded}>▶</ExpandArrow>
    </UserResultHeader>

    {expanded && (
      <>
        <UserResultDetails>
          {customer.id && <CopyField label="User ID" value={customer.id} />}
          {customer.email && <CopyField label="Email" value={customer.email} />}
          {customer.phone && <CopyField label="Phone" value={customer.phone} />}
        </UserResultDetails>

        <UserTicketsSection>
          <UserTicketsHeader>
            Tickets ({(customer.tickets?.items || []).length})
          </UserTicketsHeader>
          <CustomerTickets
            tickets={customer.tickets?.items}
            activeTicketId={activeTicketId}
            onTicketClick={onTicketClick}
          />
        </UserTicketsSection>
      </>
    )}
  </UserResultCard>
);

const CustomerResults = ({ customers, activeTicketId, onTicketClick }) => {
  // Single customer: auto-expand
  const [expandedId, setExpandedId] = useState(
    customers.length === 1 ? customers[0].id : null,
  );

  return (
    <>
      {customers.map(cust => (
        <UserCard
          key={cust.id}
          customer={cust}
          expanded={expandedId === cust.id}
          onToggle={() => setExpandedId(prev => (prev === cust.id ? null : cust.id))}
          activeTicketId={activeTicketId}
          onTicketClick={onTicketClick}
        />
      ))}
    </>
  );
};

export default CustomerResults;
